import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiMessageCircle, FiTrash2 } from 'react-icons/fi';
import { getCharacterConversations, deleteConversation } from '../services/chatService';

const formatRelativeDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

  if (diffDays === 0) return `Today at ${timeStr}`;
  if (diffDays === 1) return `Yesterday at ${timeStr}`;
  if (diffDays < 7) return `${diffDays} days ago at ${timeStr}`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago at ${timeStr}`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ` at ${timeStr}`;
};

const HistoryModal = ({
  isOpen,
  onClose,
  characterId,
  characterName,
  firebaseUser,
  onSelect,
  currentConversationId,
  refreshKey = 0,
}) => {
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [nextPageCursor, setNextPageCursor] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const loadConversations = useCallback(async (cursor = null) => {
    if (!firebaseUser || !characterId) return;
    setIsLoading(true);
    if (!cursor) setInitialLoading(true);
    setError(null);
    try {
      const token = await firebaseUser.getIdToken();
      const data = await getCharacterConversations(token, characterId, 'archived', cursor, 10);
      
      if (cursor) {
        setConversations(prev => [...prev, ...(data.conversations || [])]);
      } else {
        setConversations(data.conversations || []);
      }
      setNextPageCursor(data.nextPageCursor || null);
    } catch (err) {
      console.error('[HistoryModal] Error loading history:', err);
      setError('Failed to load history.');
    } finally {
      setIsLoading(false);
      setInitialLoading(false);
    }
  }, [firebaseUser, characterId]);

  useEffect(() => {
    if (isOpen) {
      loadConversations(null);
    }
  }, [isOpen, characterId, refreshKey, loadConversations]);

  const handleDelete = async (e, convId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;

    if (!firebaseUser) return;
    setDeletingId(convId);
    try {
      const token = await firebaseUser.getIdToken();
      await deleteConversation(token, convId);
      setConversations(prev => prev.filter(c => c.id !== convId));
    } catch (err) {
      console.error('[HistoryModal] Error deleting conversation:', err);
      alert('Failed to delete conversation.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSelect = (convId) => {
    onSelect(convId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[80vh] md:max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <span className="truncate">History with {characterName}</span>
          </h2>
          <button
            onClick={onClose}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            title="Close"
          >
            <FiX size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1 bg-gray-50">
          {error && (
            <div className="text-red-500 text-sm mb-4 text-center p-2 bg-red-50 rounded">
              {error}
            </div>
          )}

          {initialLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <FiMessageCircle size={40} className="mx-auto mb-3 opacity-30" />
              <p>No chat history found.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {conversations.map((conv) => {
                const isCurrent = conv.id === currentConversationId;
                return (
                  <div
                    key={conv.id}
                    onClick={() => handleSelect(conv.id)}
                    className={`
                      group relative flex flex-col p-4 rounded-xl shadow-sm border cursor-pointer transition-all duration-200
                      ${isCurrent 
                        ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-300' 
                        : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md'
                      }
                    `}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-sm text-gray-800 truncate pr-4">
                        {conv.title || conv.summary || 'Chat'}
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap pt-0.5">
                        {formatRelativeDate(conv.updatedAt || conv.createdAt)}
                      </span>
                    </div>

                    <div className="text-sm text-gray-500 line-clamp-2 pr-8">
                      {/* Note: if backend doesn't send preview, we just show a consistent string or hide it */}
                      {conv.lastMessagePreview || 'Messages exist in this chat.'}
                    </div>

                    <button
                      onClick={(e) => handleDelete(e, conv.id)}
                      disabled={deletingId === conv.id}
                      className={`
                        absolute right-3 bottom-3 p-2 rounded-md
                        ${deletingId === conv.id 
                          ? 'text-gray-400' 
                          : 'text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity'
                        }
                      `}
                      title="Delete chat"
                    >
                      {deletingId === conv.id ? (
                        <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full" />
                      ) : (
                        <FiTrash2 size={16} />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {!initialLoading && nextPageCursor && (
            <div className="mt-6 mb-2 flex justify-center">
              <button
                onClick={() => loadConversations(nextPageCursor)}
                disabled={isLoading}
                className="px-6 py-2.5 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors shadow-sm"
              >
                {isLoading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryModal;
